import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { localZoneName, parseReleaseInput, releaseInputValue } from '../../utils/comingSoon';
import { cn } from '../../utils/cn';

interface WindowFieldsProps {
  startsAt: number | null;
  endsAt: number | null;
  onChange: (next: { starts_at: number | null; ends_at: number | null }) => void;
  nowSec: number;
  disabled?: boolean;
}

export interface WindowProblems {
  starts?: string;
  ends?: string;
}

/** What stops a start/end pair from being saved. An end in the past is refused because it would never run. */
export function windowProblems(startsAt: number | null, endsAt: number | null, nowSec: number): WindowProblems {
  const problems: WindowProblems = {};
  if (endsAt !== null && startsAt !== null && endsAt <= startsAt) problems.ends = 'The end has to be after the start.';
  else if (endsAt !== null && endsAt <= nowSec) problems.ends = 'This end date has already passed, so it would never run.';
  return problems;
}

interface DateTimeFieldProps {
  id: string;
  label: string;
  help: string;
  value: number | null;
  onChange: (value: number | null) => void;
  problem?: string;
  disabled: boolean;
}

/**
 * A `datetime-local` bound to unix seconds. The typed text is kept locally so
 * a half-typed date is not thrown away, and only a complete one is committed.
 */
const DateTimeField = ({ id, label, help, value, onChange, problem, disabled }: DateTimeFieldProps) => {
  const [text, setText] = useState(releaseInputValue(value));
  const [typingError, setTypingError] = useState<string | null>(null);

  useEffect(() => {
    // Resync from outside only when the committed value really changed.
    const parsed = parseReleaseInput(text, 0);
    const current = parsed.ok ? parsed.releaseAt : undefined;
    if (current !== value) {
      setText(releaseInputValue(value));
      setTypingError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const change = (next: string) => {
    setText(next);
    // A zero "now" skips the future-only check that coming-soon windows need;
    // a start date in the past is fine, it just means "already started".
    const parsed = parseReleaseInput(next, 0);
    if (parsed.ok) {
      setTypingError(null);
      onChange(parsed.releaseAt);
    } else {
      setTypingError(parsed.error);
    }
  };

  const shown = typingError ?? problem;

  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs font-medium text-glass-400 mb-1.5">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          id={id}
          type="datetime-local"
          className={cn('glass-input w-full tabular-nums', shown && 'border-red-500/50')}
          value={text}
          onChange={(e) => change(e.target.value)}
          disabled={disabled}
          aria-invalid={!!shown}
          aria-describedby={`${id}-help`}
        />
        {value !== null && !disabled && (
          <button type="button" onClick={() => change('')} className="btn-icon btn-icon-sm shrink-0" title={`Clear ${label.toLowerCase()}`} aria-label={`Clear ${label.toLowerCase()}`}>
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <p id={`${id}-help`} className={cn('text-xs mt-1', shown ? 'text-red-400' : 'text-glass-500')}>{shown ?? help}</p>
    </div>
  );
};

/** Optional start and end dates: a sale announcement that switches itself on and off. */
export const WindowFields = ({ startsAt, endsAt, onChange, nowSec, disabled = false }: WindowFieldsProps) => {
  const problems = windowProblems(startsAt, endsAt, nowSec);
  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-2">
        <DateTimeField
          id="schedule-starts"
          label="Starts"
          help={startsAt === null ? 'Empty starts it straight away.' : startsAt <= nowSec ? 'Already started.' : 'It waits until then.'}
          value={startsAt}
          onChange={(next) => onChange({ starts_at: next, ends_at: endsAt })}
          problem={problems.starts}
          disabled={disabled}
        />
        <DateTimeField
          id="schedule-ends"
          label="Ends"
          help={endsAt === null ? 'Empty runs it until someone pauses it.' : 'It pauses itself then.'}
          value={endsAt}
          onChange={(next) => onChange({ starts_at: startsAt, ends_at: next })}
          problem={problems.ends}
          disabled={disabled}
        />
      </div>
      <p className="text-xs text-glass-500">Times are in {localZoneName()}.</p>
    </div>
  );
};
